import React, { useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItemButton, ListItemText, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Close, MenuBookOutlined, Search } from '@mui/icons-material';
import { researcherFontFamily, researcherSectionSx } from '../../researcher/researcherPresentation';
import { findTutorials, researcherTutorials } from './tutorialContent';
import TutorialVisual from './TutorialVisual';

function TutorialLibrary({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('invite');
  const articleRef = useRef<HTMLElement>(null);
  const matches = findTutorials(query);
  const selected = matches.find(guide => guide.id === selectedId) || matches[0];
  const chooseGuide = (id: string) => {
    setSelectedId(id);
    if (articleRef.current) articleRef.current.scrollTop = 0;
  };

  return <>
    <DialogTitle id="tutorials-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <MenuBookOutlined color="primary" />
      <Box sx={{ flex: 1 }}><Typography component="span" sx={{ fontSize: 22, fontWeight: 700 }}>Tutorials</Typography>
        <Typography color="text.secondary" variant="body2">A little help with your next step.</Typography></Box>
      <IconButton onClick={onClose} aria-label="Close tutorials"><Close /></IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 0 }}>
      <Box component="nav" aria-label="Tutorial topics" sx={{ width: { md: 290 }, flexShrink: 0, p: 2, bgcolor: '#f8f9fb', overflowY: { md: 'auto' }, borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
        <TextField label="Find a tutorial" placeholder="Try invite or dates" size="small" fullWidth value={query}
          onChange={event => { setQuery(event.target.value); if (articleRef.current) articleRef.current.scrollTop = 0; }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: query ? <InputAdornment position="end"><IconButton size="small" aria-label="Clear tutorial search" onClick={() => setQuery('')}><Close fontSize="small" /></IconButton></InputAdornment> : undefined }} />
        {!!matches.length && <TextField select label="Topic" size="small" fullWidth value={selected.id} onChange={event => chooseGuide(event.target.value)} sx={{ mt: 2, display: { xs: 'block', md: 'none' } }}>
          {matches.map(guide => <MenuItem key={guide.id} value={guide.id}>{guide.title}</MenuItem>)}
        </TextField>}
        <List sx={{ mt: 1, display: { xs: 'none', md: 'block' } }}>
          {matches.map(guide => <ListItemButton key={guide.id} selected={selected.id === guide.id} aria-current={selected.id === guide.id ? 'page' : undefined}
            onClick={() => chooseGuide(guide.id)} sx={{ borderRadius: 2, mb: 0.5, '&.Mui-selected': { bgcolor: '#f6eaea' } }}>
            <ListItemText primary={guide.title} primaryTypographyProps={{ fontSize: 14, fontWeight: selected.id === guide.id ? 650 : 500 }} />
          </ListItemButton>)}
        </List>
      </Box>
      <Box component="article" ref={articleRef} aria-labelledby={selected ? 'tutorial-article-title' : undefined} sx={{ p: { xs: 2, sm: 3.5 }, flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {selected ? <>
          <Typography component="h2" id="tutorial-article-title" sx={{ fontSize: 24, fontWeight: 700 }}>{selected.title}</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>{selected.summary}</Typography>
          <Paper variant="outlined" sx={{ ...researcherSectionSx, mt: 2.5, bgcolor: '#f8f9fb' }}>
            <Typography variant="overline" color="text.secondary">Where to start</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selected.where}</Typography>
          </Paper>
          <TutorialVisual id={selected.id} title={selected.title} />
          <Box component="ol" sx={{ pl: 3, my: 3, '& li': { pl: 0.75, mb: 1.75 }, '& li::marker': { color: 'primary.main', fontWeight: 700 } }}>
            {selected.steps.map(step => <li key={step}><Typography sx={{ lineHeight: 1.7, fontSize: 15 }}>{step}</Typography></li>)}
          </Box>
          <Stack spacing={2}>{selected.notes.map(note => <Box key={note.title}>
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 650 }}>{note.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>{note.text}</Typography>
          </Box>)}</Stack>
          {!!selected.related?.length && <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 650, mb: 1 }}>Related tutorials</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">{selected.related.map(id => researcherTutorials.find(guide => guide.id === id)).map(guide => guide &&
              <Button key={guide.id} size="small" variant="outlined" onClick={() => { setQuery(''); chooseGuide(guide.id); }}>{guide.title}</Button>
            )}</Stack>
          </Box>}
        </> : <Box role="status" sx={{ py: 6, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600 }}>No tutorials found</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>Try a shorter search, or browse all topics.</Typography>
          <Button onClick={() => setQuery('')} sx={{ mt: 2 }}>Show all tutorials</Button>
        </Box>}
      </Box>
    </DialogContent>
  </>;
}

export default function ResearcherTutorials() {
  const [open, setOpen] = useState(false);
  return <>
    <Tooltip title="Tutorials"><Button color="primary" aria-label="Tutorials" onClick={() => setOpen(true)} sx={{ minWidth: 40, gap: 0.75 }}>
      <MenuBookOutlined fontSize="small" /><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Tutorials</Box>
    </Button></Tooltip>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg" aria-labelledby="tutorials-title"
      PaperProps={{ sx: { height: 'min(780px, 90dvh)', m: { xs: 1, sm: 3 }, width: { xs: 'calc(100% - 16px)', sm: '100%' }, borderRadius: 3, fontFamily: researcherFontFamily } }}>
      {open && <TutorialLibrary onClose={() => setOpen(false)} />}
    </Dialog>
  </>;
}
